import React from 'react';
import { ClientConfig } from '../types/profile';
import { useLocalization } from '../engine/localization/context';
import { BrandLogo } from '../engine/branding/BrandLogo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { CheckCircle2 } from 'lucide-react';

interface HeaderProps {
  config: ClientConfig;
  onToggleClient?: () => void;
  activeClientId: string;
}

export const Header: React.FC<HeaderProps> = ({
  config,
}) => {
  const { resolve, t } = useLocalization();
  const identity = config.identity;

  const displayName = resolve(identity.name);
  const tagline = resolve(identity.tagline);
  const description = resolve(identity.description);
  const badgeText = identity.badge ? resolve(identity.badge) : null;

  return (
    <header className="relative flex flex-col items-center text-center pt-2 pb-6 px-4">
      {/* Top bar controls: Clean Language Switcher (Customer-visible Demo Mode removed) */}
      <div className="w-full flex items-center justify-end mb-6">
        <LanguageSwitcher />
      </div>

      {/* Brand Logo with Premium Presentation */}
      <div className="mb-4">
        <BrandLogo
          src={identity.logoUrl}
          alt={displayName}
          styleMode={config.branding.logoStyle || 'glow'}
          motion={config.branding.logoMotion || 'glow'}
          className="w-24 h-24 sm:w-28 sm:h-28"
          maxWidth={config.branding.logoMaxWidth}
          maxHeight={config.branding.logoMaxHeight}
          background={config.branding.logoBackground}
          fallbackText={config.slug.toUpperCase()}
        />
      </div>

      {/* Verified Badge */}
      {identity.verified && (
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold mb-3 tracking-wide"
          title={t('verified.tooltip')}
        >
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          <span>{badgeText || t('verified.tooltip')}</span>
        </div>
      )}

      {/* Identity Name */}
      <h1 className="text-xl xs:text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2 leading-snug break-words max-w-full">
        {displayName}
      </h1>

      {/* Tagline */}
      {tagline && (
        <p className="text-xs xs:text-sm sm:text-base font-semibold text-sky-400/95 max-w-lg mb-2 leading-relaxed break-words">
          {tagline}
        </p>
      )}

      {/* Description */}
      {description && (
        <p className="text-[11px] xs:text-xs sm:text-sm text-slate-300/90 max-w-md leading-relaxed mx-auto break-words">
          {description}
        </p>
      )}
    </header>
  );
};
